import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, X } from 'lucide-react'
import { portalApi, type PortalSessionDto, type PortalStaffMemberDto } from '@/api/portal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePortalMoney } from '@/hooks/useMoney'

interface Props {
  session: PortalSessionDto
  staff: PortalStaffMemberDto[]
  /** The training company takes payment by card when a place is booked (for priced sessions). */
  requiresPayment: boolean
  dateLine: string
  onClose: () => void
  onBooked: () => void
}

function errorMessage(e: unknown) {
  const data = (e as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

export default function BookStaffModal({ session, staff, requiresPayment, dateLine, onClose, onBooked }: Props) {
  const { fmt } = usePortalMoney()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [done, setDone] = useState(false)

  const booked = useMemo(() => new Set(session.bookedStaffIds), [session.bookedStaffIds])
  const active = staff.filter(s => s.isActive)
  const visible = active.filter(s => `${s.firstName} ${s.lastName} ${s.email}`.toLowerCase().includes(search.trim().toLowerCase()))
  const available = visible.filter(s => !booked.has(s.id))

  const free = session.availablePlaces
  const price = session.pricePerPerson ?? 0
  const total = price * selected.length
  const over = selected.length > free
  const pay = requiresPayment && price > 0

  const mutation = useMutation({
    mutationFn: () => portalApi.bookSession(session.id, selected).then(r => r.data),
    onSuccess: res => {
      onBooked()
      // Straight to the card payment page when the training company takes payment at booking (a full-page redirect, so it
      // can't be blocked like a pop-up).
      if (res.checkoutUrl) { window.location.href = res.checkoutUrl; return }
      setDone(true)
    },
  })

  const allSelected = available.length > 0 && available.every(s => selected.includes(s.id))
  const toggle = (id: string) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-semibold text-slate-900">Book staff onto {session.courseName}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{dateLine}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close"><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-6 space-y-3">
            <p className="flex items-center gap-2 text-green-700 font-semibold"><CheckCircle2 size={18} /> Booked</p>
            <p className="text-sm text-slate-600">
              {selected.length} place{selected.length === 1 ? ' is' : 's are'} confirmed. We've emailed you a confirmation, and each person has been told too.
              {price > 0 && ' Payment is arranged with us separately.'}
            </p>
            <Button onClick={onClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-3 overflow-y-auto">
              <p className="text-xs text-slate-500">
                {free} place{free === 1 ? '' : 's'} left · {price > 0 ? `${fmt(price)} per person` : 'Free'}
              </p>
              <div className="flex items-center gap-2">
                <Input placeholder="Search your team…" value={search} onChange={e => setSearch(e.target.value)} />
                <Button
                  type="button" size="sm" variant="outline" className="shrink-0"
                  disabled={available.length === 0}
                  onClick={() => setSelected(s => allSelected
                    ? s.filter(id => !available.some(a => a.id === id))
                    : Array.from(new Set([...s, ...available.map(a => a.id)])))}
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </Button>
              </div>

              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {visible.length === 0 && <p className="p-3 text-sm text-slate-400">No staff found.</p>}
                {visible.map(s => {
                  const already = booked.has(s.id)
                  return (
                    <label key={s.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${already ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}`}>
                      <input type="checkbox" disabled={already} checked={selected.includes(s.id)} onChange={() => toggle(s.id)} className="accent-indigo-600" />
                      <span className="flex-1 min-w-0">
                        <span className="font-medium text-slate-800">{s.firstName} {s.lastName}</span>
                        <span className="block text-xs text-slate-400 truncate">{s.email}</span>
                      </span>
                      {already && <span className="text-xs text-slate-500">Already booked</span>}
                    </label>
                  )
                })}
              </div>

              {over && <p className="text-sm text-red-600">That's {selected.length} people but only {free} place{free === 1 ? '' : 's'} left. Choose fewer people.</p>}
              {mutation.isError && <p className="text-sm text-red-600">{errorMessage(mutation.error)}</p>}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 space-y-3">
              {selected.length > 0 && price > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{selected.length} × {fmt(price)}</span>
                  <span className="font-semibold text-slate-900">{fmt(total)}</span>
                </div>
              )}
              {selected.length > 0 && price > 0 && (
                <p className="text-xs text-slate-500">
                  {pay
                    ? "You'll be taken to pay by card to confirm these places."
                    : 'Places are confirmed straight away. Payment is arranged with us separately.'}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                <Button onClick={() => mutation.mutate()} disabled={selected.length === 0 || over || mutation.isPending}>
                  {mutation.isPending
                    ? 'Booking…'
                    : pay
                      ? `Continue to payment${selected.length ? ` (${fmt(total)})` : ''}`
                      : `Book ${selected.length || ''} place${selected.length === 1 ? '' : 's'}`.replace('  ', ' ')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
